import React, { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Modal from "components/Elements/Modalv2";
import Button, { ButtonType } from "components/Elements/Buttonv2";
import Input from "components/Elements/Inputv2";
import ApiService from "services/api.service";
import { Group, GroupType } from "types/Group";

// Sentinel value for the "create a brand new group" option in the picker.
const NEW_GROUP = "__new__";

// How many add-requests to fire at once. Selection can span pages, so we chunk
// to avoid opening hundreds of parallel requests at once. The add endpoint is
// additive and treats an already-present contact as a no-op (409), so retries
// and duplicates are safe.
const ADD_CHUNK = 50;

interface AddToGroupModalProps {
  isOpen: boolean;
  contactIds: string[];
  onClose: () => void;
  onAdded: (count: number) => void;
}

const AddToGroupModal: FC<AddToGroupModalProps> = ({
  isOpen,
  contactIds,
  onClose,
  onAdded,
}) => {
  // Only manual groups can take hand-picked members. Automatic groups recompute
  // their membership from rules, so adding contacts to one would be wiped out.
  const [manualGroups, setManualGroups] = useState<Group[]>([]);
  const [target, setTarget] = useState<string>("");
  const [newGroupName, setNewGroupName] = useState("");
  const [isWorking, setIsWorking] = useState(false);

  const loadGroups = async () => {
    try {
      const {
        data: { data },
      } = await ApiService.get<{ data: Group[] }>({ url: "/groups?take=200" });
      const manual = (data ?? []).filter((g) => g.type === GroupType.MANUAL);
      setManualGroups(manual);
      // Default to the first existing manual group, or straight to "new" when
      // there are none so the user always has a path forward.
      setTarget(manual.length ? manual[0].id : NEW_GROUP);
    } catch {
      setManualGroups([]);
      setTarget(NEW_GROUP);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setNewGroupName("");
      setIsWorking(false);
      loadGroups();
    }
  }, [isOpen]);

  const addContactsToGroup = async (groupId: string) => {
    let added = 0;
    for (let i = 0; i < contactIds.length; i += ADD_CHUNK) {
      const chunk = contactIds.slice(i, i + ADD_CHUNK);
      const results = await Promise.allSettled(
        chunk.map((customerId) =>
          ApiService.post({
            url: `/groups/${groupId}/contacts`,
            options: { customerId },
          })
        )
      );
      // A rejected request is almost always "already in this group" (409),
      // which we treat as a skip rather than a failure.
      added += results.filter((r) => r.status === "fulfilled").length;
    }
    return added;
  };

  const handleConfirm = async () => {
    if (!contactIds.length) return;

    if (target === NEW_GROUP && !newGroupName.trim()) {
      toast.error("Enter a name for the new group");
      return;
    }
    if (target !== NEW_GROUP && !target) {
      toast.error("Choose a group");
      return;
    }

    setIsWorking(true);
    try {
      let groupId = target;
      let groupName =
        manualGroups.find((g) => g.id === target)?.name ?? "the group";

      if (target === NEW_GROUP) {
        const { data } = await ApiService.post<{ id: string }>({
          url: "/groups",
          options: { name: newGroupName.trim(), type: GroupType.MANUAL },
        });
        groupId = data.id;
        groupName = newGroupName.trim();
      }

      const added = await addContactsToGroup(groupId);

      const skipped = contactIds.length - added;
      toast.success(
        `Added ${added} contact${added === 1 ? "" : "s"} to "${groupName}"` +
          (skipped > 0 ? ` (${skipped} already in the group)` : "")
      );
      // Other pages listen for this to refresh their group lists/counts.
      window.dispatchEvent(new Event("leadstats:groups-changed"));
      onAdded(added);
      onClose();
    } catch {
      toast.error("Failed to add contacts to the group");
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      panelClassName="w-[520px] px-[32px] pt-[28px] pb-[24px]"
    >
      <div className="font-inter text-[14px] text-themed-text flex flex-col gap-[18px]">
        <div className="text-[18px] font-semibold leading-[26px]">
          Add {contactIds.length} contact{contactIds.length === 1 ? "" : "s"} to
          a group
        </div>

        <div className="flex flex-col gap-[8px]">
          <div className="text-themed-textSecondary text-[13px]">Group</div>
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="w-full px-[12px] py-[8px] border border-themed-border rounded-sm bg-themed-card text-[14px] leading-[22px] outline-none focus:border-[#6366F1]"
          >
            <option value={NEW_GROUP}>+ Create a new group</option>
            {manualGroups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>

        {target === NEW_GROUP && (
          <div className="flex flex-col gap-[8px]">
            <div className="text-themed-textSecondary text-[13px]">
              New group name
            </div>
            <Input
              value={newGroupName}
              onChange={setNewGroupName}
              placeholder="e.g. Spring renewal outreach"
            />
          </div>
        )}

        <div className="flex justify-end gap-[10px]">
          <Button type={ButtonType.SECONDARY} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type={ButtonType.PRIMARY}
            disabled={isWorking}
            onClick={handleConfirm}
          >
            {isWorking ? "Adding..." : "Add to group"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddToGroupModal;
